import React from 'react';
import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/20/solid';

const actionNames = {
  search: 'Searches',
  load: 'Views',
  call: 'Phone Calls',
  website: 'Website Clicks',
  directions: 'Directions',
  save: 'Listing Saves',
  checkin: 'Check-Ins',
  reservation: 'Reservations',
  hours: 'Hours Viewed',
  social: 'Social Media Clicks',
  sip: 'Sips',
  list: 'Lists Added To',
  nearby: 'Listing Nearby',
  discover: 'Discover Nearby',
};

const actionKeys = Object.keys(actionNames);

function calculateTrend(timeSeries, actionKey) {
  if (!timeSeries || timeSeries.length < 2) return null;

  const mid = Math.floor(timeSeries.length / 2);
  const firstHalf = timeSeries.slice(0, mid);
  const secondHalf = timeSeries.slice(mid);

  const firstSum = firstHalf.reduce((sum, d) => sum + (d[actionKey] || 0), 0);
  const secondSum = secondHalf.reduce((sum, d) => sum + (d[actionKey] || 0), 0);

  if (firstSum === 0 && secondSum === 0) return null;
  if (firstSum === 0) return { direction: 'up', value: 100 };

  const pct = Math.round(((secondSum - firstSum) / firstSum) * 100);
  if (pct === 0) return null;
  return { direction: pct > 0 ? 'up' : 'down', value: Math.abs(pct) };
}

export default function ActionBreakdownTable({ metrics, timeSeries }) {
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <h4 className="text-base font-semibold text-gray-900">Action Breakdown</h4>
      <p className="mt-1 text-sm text-gray-500">Detailed counts by action type</p>
      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead>
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Action
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Count
              </th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trend
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {actionKeys.map((key) => {
              const count = metrics?.[key] || 0;
              const trend = calculateTrend(timeSeries, key);

              return (
                <tr key={key}>
                  <td className="px-4 py-3 text-sm text-gray-900">{actionNames[key]}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                    {count.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {trend ? (
                      <span
                        className={`inline-flex items-center text-sm font-medium ${
                          trend.direction === 'up' ? 'text-green-600' : 'text-red-600'
                        }`}
                      >
                        {trend.direction === 'up' ? (
                          <ArrowUpIcon className="h-4 w-4 mr-0.5" />
                        ) : (
                          <ArrowDownIcon className="h-4 w-4 mr-0.5" />
                        )}
                        {trend.value}%
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">&mdash;</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
